/**
 * Brand label composer — iter v29 r201
 *
 * Risolve la duplicazione del nome agente nei contenuti social.
 *
 * Esempio del problema:
 *   user.name = "Luca Ferrari"
 *   agency_name = "Casa Bella di Luca Ferrari"  (immesso dall'utente)
 *   → senza questa utility, i template stampavano:
 *     "Luca Ferrari · Casa Bella di Luca Ferrari"   ← duplicato
 *
 * Con `composeAgentBrand("Luca Ferrari", "Casa Bella di Luca Ferrari")` →
 *   { primary: "Casa Bella di Luca Ferrari", secondary: null, includesAgent: true }
 *
 * Il template dovrebbe mostrare solo `primary` quando `includesAgent === true`.
 */

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function isNameContainedIn(name, container) {
  if (!name || !container) return false;
  const n = name.trim();
  const c = container.trim();
  if (n.length < 3 || c.length < n.length) return false;
  // Match come parola intera (con boundary). Case-insensitive.
  try {
    return new RegExp(`\\b${escapeRegex(n)}\\b`, "i").test(c);
  } catch {
    return c.toLowerCase().includes(n.toLowerCase());
  }
}

/**
 * @param {string} agentName  Nome personale dell'agente (es. "Luca Ferrari")
 * @param {string} agencyName Nome del brand/agenzia (es. "Casa Bella di Luca Ferrari")
 * @returns {{
 *   primary: string,         // Etichetta principale (mostra in grande)
 *   secondary: string|null,  // Etichetta secondaria (mostra sotto), null se duplicato
 *   includesAgent: boolean,  // true se agent_name è già dentro primary
 * }}
 */
export function composeAgentBrand(agentName, agencyName) {
  const a = (agentName || "").trim();
  const b = (agencyName || "").trim();

  if (!a && !b) return { primary: "", secondary: null, includesAgent: false };
  if (!a) return { primary: b, secondary: null, includesAgent: false };
  if (!b) return { primary: a, secondary: null, includesAgent: true };

  // Identici (case-insensitive)
  if (a.toLowerCase() === b.toLowerCase()) {
    return { primary: a, secondary: null, includesAgent: true };
  }

  // Agent_name è contenuto in agency_name → mostra solo agency
  if (isNameContainedIn(a, b)) {
    return { primary: b, secondary: null, includesAgent: true };
  }

  // Agency_name è contenuto in agent_name (raro) → mostra solo agent
  if (isNameContainedIn(b, a)) {
    return { primary: a, secondary: null, includesAgent: true };
  }

  // Nomi diversi e indipendenti → mostra entrambi
  return { primary: a, secondary: b, includesAgent: false };
}
